/**
 * The readiness view (SCH-2963): "is this app ready, can it build, and what is the one next
 * action", read from the backend readiness module — the one judge — by the dashboard and agents.
 *
 * GET /v1/mobile-apps/:id/readiness[?platforms=IOS,ANDROID&environment=]        (super-admin)
 * GET /internal/mobile-apps/:id/readiness[?platforms=IOS,ANDROID&environment=]  (x-api-key)
 *
 * Without `platforms` it is the stored view: capability readiness and the next action from stored
 * facts, no vendor called. With `platforms` it also runs the live build gate for each one against
 * `environment` (default `production`, as the build-readiness route), and the next action sees it.
 */

import { z } from 'zod'
import { AppReadinessNextActionSchema, MobileAppReadinessSchema } from '../../schemas/entities/mobile-app-readiness'
import { BuildReadinessPlatformSchema, BuildReadinessReportSchema } from './build-readiness'

/** One platform the gate could not answer for: a read failure, never a pass. */
export const AppReadinessUnreadablePlatformSchema = z.object({
  platform: BuildReadinessPlatformSchema,
  message: z.string()
})
export type AppReadinessUnreadablePlatform = z.infer<typeof AppReadinessUnreadablePlatformSchema>

/** The live gate's answer, when it was asked for. */
export const AppReadinessGateSchema = z.object({
  environment: z.enum(['development', 'preview', 'production']),
  /** One report per platform the gate answered, iOS before Android. */
  reports: z.array(BuildReadinessReportSchema),
  /** Platforms the gate could not read. The view never calls these ready. */
  unreadable: z.array(AppReadinessUnreadablePlatformSchema),
  /** Every asked platform answered and every report is ready. */
  ready: z.boolean()
})
export type AppReadinessGate = z.infer<typeof AppReadinessGateSchema>

export const AppReadinessViewSchema = z.object({
  appId: z.string(),
  /** Stored capability readiness: the same answer every app read carries. */
  readiness: MobileAppReadinessSchema,
  /** Null when no platform was asked for: the gate runs on demand only. */
  gate: AppReadinessGateSchema.nullable(),
  nextAction: AppReadinessNextActionSchema
})
export type AppReadinessView = z.infer<typeof AppReadinessViewSchema>

/** `platforms` is a comma list (`IOS`, `ANDROID`, or both); repeated or unknown values are refused. */
export const GetAppReadinessQuerySchema = z.object({
  platforms: z
    .string()
    .optional()
    .transform(value => (value ? value.split(',').map(part => part.trim().toUpperCase()) : []))
    .pipe(
      z
        .array(BuildReadinessPlatformSchema)
        .refine(list => new Set(list).size === list.length, { message: 'platforms must not repeat' })
    ),
  environment: z.enum(['development', 'preview', 'production']).optional()
})
export type GetAppReadinessQuery = z.infer<typeof GetAppReadinessQuerySchema>

export const GetAppReadinessResponseSchema = z.object({
  data: AppReadinessViewSchema,
  message: z.string()
})
export type GetAppReadinessResponse = z.infer<typeof GetAppReadinessResponseSchema>
